import { useState } from "react";
import { motion } from "framer-motion";
import {
  Heart,
  Coffee,
  Sun,
  Moon,
  Music,
  Feather,
  Sparkles,
  Star,
  Cherry,
  Flame,
  Cloud,
  Smile,
} from "lucide-react";
import CoupleAccent from "@/components/CoupleAccent";

const REASONS = [
  {
    icon: Sun,
    title: "your laugh",
    body: "it changes the temperature of the room. i've never heard anything that sounds more like home. the small one you do when you're trying not to encourage a bad joke, and the big one that comes out when i finally catch you off guard — both are mine to chase forever.",
  },
  {
    icon: Smile,
    title: "the tiny hair that keeps peeking out of your nose",
    body: "and — this is our secret — one of mine does the exact same thing. we're secretly twinning. every time i notice it, i grin like an idiot, because it's a little private thing only we know about. i hope it never fully goes away. it feels like a small proof that we belong to each other.",
  },
  {
    icon: Star,
    title: "the little scar on your eyebrow",
    body: "the one you sometimes trace without realising. it is one of my favourite places on your entire face. it holds a piece of your story that existed before me, and somehow it makes me love you more — like it's a tiny bookmark reminding me that i got to meet you at all.",
  },
  {
    icon: Sparkles,
    title: "the mole only i know about",
    body: "our little secret. i've memorized it like a small hidden star on a private map — a place on you that belongs only to us. every time i see it, i feel unreasonably lucky that of all the people in the world, i'm the one who gets to know it's there.",
  },
  {
    icon: Coffee,
    title: "your enormous heart",
    body: "you think about everyone. genuinely. their needs, their small wants, the things they haven't even said out loud yet — you notice all of it. and the people you love? they get the softest, most protective, most attentive version of you. you put the people you care about above everything, without keeping score, without waiting to be thanked. the world is not kind enough to people like you, and yet you keep pouring it out anyway. i'm in constant awe of it.",
  },
  {
    icon: Cherry,
    title: "the way your nose wrinkles when you're mad at me",
    body: "i know i'm not supposed to think it's cute when you're upset. i know. but there is this specific little wrinkle that shows up right at the top of your nose, and my brain betrays me every time. i deserve the frown. i also love the frown. both things can be true.",
  },
  {
    icon: Cloud,
    title: "the pout before the smile",
    body: "when you're trying to stay grumpy but a smile is already sneaking up on you — that tiny in-between moment where your mouth is fighting itself — it might be the single most beautiful expression a face can make. every time it happens, my whole day quietly rearranges itself around it.",
  },
  {
    icon: Moon,
    title: "your softness",
    body: "even when the world is loud, you stay gentle. even when i've been the loud thing. you hold everyone — including me — with a kind of tenderness i don't think i've properly earned. i want to spend the rest of my life being worthy of the way you love people.",
  },
  {
    icon: Music,
    title: "your patience",
    body: "you have loved me through versions of myself i wouldn't have loved. you kept the door open when it was very reasonable to close it. that patience is not lost on me — not for a second. i see it. i've always seen it. and it will never be something i take for granted again.",
  },
  {
    icon: Feather,
    title: "your honesty",
    body: "you tell me the truth even when it costs you. even when you know it'll be uncomfortable. even when it would be easier for you to just swallow it. that is the rarest, bravest kind of love, and i want to be a person who can hold it as gently as you hand it to me.",
  },
  {
    icon: Flame,
    title: "the fire only i get to see",
    body: "the goofy voices, the wildly specific opinions, the sudden bursts of energy, the songs you sing when you think no one's listening. the version of you that only comes out when you feel safe. that version is my favourite thing on this planet, and i never want you to feel unsafe enough to hide her again.",
  },
  {
    icon: Heart,
    title: "just… you",
    body: "everything above. and everything i haven't managed to put into words yet. the way you pause before you laugh. the way you say my name when you're happy, and the way you say it when you're upset. every ordinary detail is my favourite thing. i love the imperfections that make you perfect. every single one of them.",
  },
];

const Card = ({ reason, index }) => {
  const [open, setOpen] = useState(false);
  const Icon = reason.icon;
  return (
    <motion.button
      data-testid="memory-card"
      onClick={() => setOpen((o) => !o)}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08 }}
      whileHover={{ y: -4 }}
      className="group relative text-left rounded-2xl border border-[#E8D8D8] bg-white/75 backdrop-blur p-6 sm:p-7 min-h-[210px] overflow-hidden shadow-sm hover:shadow-rose-100 transition-shadow"
    >
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-full bg-[#F5EBEB] flex items-center justify-center text-[#C28080]">
          <Icon className="w-5 h-5" />
        </div>
        <span className="font-hand text-lg text-rose-300 opacity-0 group-hover:opacity-100 transition-opacity">
          {open ? "close" : "reveal"}
        </span>
      </div>

      <h3 className="font-serif-love text-xl sm:text-2xl mt-5 text-[#3A2C2C] italic leading-snug">
        {reason.title}
      </h3>

      <motion.p
        animate={{
          opacity: open ? 1 : 0.35,
          filter: open ? "blur(0px)" : "blur(4px)",
        }}
        transition={{ duration: 0.5 }}
        className="mt-3 text-[#7D6B6B] font-body-love text-[15px] leading-relaxed"
      >
        {reason.body}
      </motion.p>

      {!open && (
        <span className="absolute bottom-3 right-4 text-[10px] uppercase tracking-[0.3em] text-[#C28080]">
          tap to reveal
        </span>
      )}
    </motion.button>
  );
};

export const ReasonsGallery = () => {
  return (
    <section
      data-testid="reasons-section"
      className="relative py-24 sm:py-32 px-6 sm:px-12"
    >
      <CoupleAccent
        src="/couple/holding_hands.png"
        alt="us together"
        className="hidden md:block absolute bottom-6 right-4 lg:right-14 w-24 lg:w-32 rotate-[6deg] pointer-events-none"
        float={{ y: [0, -8, 0] }}
        duration={7}
      />
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-center mb-14"
        >
          <p className="font-hand text-2xl text-rose-400 mb-2">
            in case you forgot
          </p>
          <h2 className="font-serif-love text-3xl sm:text-5xl text-[#3A2C2C]">
            the imperfections that make you{" "}
            <span className="italic text-[#C28080]">perfect</span>
          </h2>
          <p className="font-body-love text-[#7D6B6B] mt-4 max-w-xl mx-auto">
            small, small things. the ones only i get to see. tap each card —
            they get more personal as you go.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {REASONS.map((r, i) => (
            <Card key={i} reason={r} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReasonsGallery;
